import { type CustomColormap, useGetCustomColormap } from "@/api/hooks/customColormaps";
import type { ColormapCreate } from "@mm/metx-workbench.meteomatics.com";
import { useMemo } from "react";

type UseResolvedLegendColormapProps = {
  customColormap?: ColormapCreate;
  customColormapId?: string;
};

type UseResolvedLegendColormapResult = {
  resolvedCustomColormap?: ColormapCreate;
  isLoading: boolean;
  shouldShowUnavailable: boolean;
};

function toLegendColormap(colormap: CustomColormap | undefined): ColormapCreate | undefined {
  if (!colormap) {
    return undefined;
  }

  const stops: [number, string][] = (colormap.stops ?? [])
    .filter((entry): entry is [number, string] => Array.isArray(entry) && entry.length === 2)
    .map(([value, color]) => [Number(value), String(color).trim()] as [number, string])
    .filter(([value, color]) => Number.isFinite(value) && color !== "");

  if (stops.length < 2) {
    return undefined;
  }

  return {
    type: colormap.type === "segmented" ? "segmented" : "continuous",
    label: colormap.label ?? "",
    stops,
  };
}

// Resolve a legend colormap from either inline data or a fetched custom colormap id,
export function useResolvedLegendColormap({
  customColormap,
  customColormapId,
}: UseResolvedLegendColormapProps): UseResolvedLegendColormapResult {
  const shouldFetchCustomColormap = !customColormap && Boolean(customColormapId);
  const { colormap: fetchedCustomColormap, isLoading: isCustomColormapLoading } = useGetCustomColormap(
    customColormapId,
    shouldFetchCustomColormap,
  );

  const resolvedCustomColormap = useMemo(
    () => customColormap ?? toLegendColormap(fetchedCustomColormap),
    [customColormap, fetchedCustomColormap],
  );

  const isLoading = shouldFetchCustomColormap && isCustomColormapLoading;
  const shouldShowUnavailable = shouldFetchCustomColormap && !isLoading && !resolvedCustomColormap;

  return {
    resolvedCustomColormap,
    isLoading,
    shouldShowUnavailable,
  };
}
